import React, { useEffect, useState } from 'react';
import { LogoLoading } from 'ensightful-koala-ui';
import { styled } from '@mui/material';

import { AllTeams, FilterSortGroupOverview, SuggestedTeams } from 'components/organisms/GroupOverView';
import { useAuth } from 'contexts/auth';
import { useInstructor } from 'contexts/instructorRole';
import { useUser2 } from 'contexts/user2';
import { useViewport } from 'contexts/viewport';
import withResponsiveLayout from 'hoc/withResponsive';
import Mobile from 'pages/GroupOverView/mobile';
import Tablet from 'pages/GroupOverView/tablet';
import Desktop from 'pages/GroupOverView/desktop';
import courseService from 'services/course';
import accountService from 'services/account';
import { Box, Drawer, Stack, Text } from 'shell/components';
import NoGroups from 'utils/assetFiles/svgs/nogroups';
import { getAverage, getProgress, getStatus, getUserPreferredFullName } from 'utils/helper';

const getDefaultSortKey = (filterKeys) => {
    return filterKeys?.group_overview?.sort_key || 'none';
};

const getDefaultStatusKey = (filterKeys) => {
    return filterKeys?.group_overview?.status_key || [];
};

const GroupOverview = () => {
    const { auth, setAuth } = useAuth();
    const { suggestions } = useInstructor();
    const { isLoading, course, groups, setCurrentGroupId, distribution, setDistribution, studentAccountUsers } =
        useUser2();
    const { width } = useViewport();

    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    const [sortKey, setSortKey] = useState(getDefaultSortKey(auth?.filter_keys));
    const [statusKey, setStatusKey] = useState(getDefaultStatusKey(auth?.filter_keys));
    const [searchBarInput, setSearchBarInput] = useState('');
    const [defaultRoute, setDefaultRoute] = useState('all-teams');
    const [initialSuggestionsGroupId, setInitialSuggestionsGroupId] = useState('');

    const viewSuggestions = (groupId) => {
        setDefaultRoute('suggested-teams');
        setInitialSuggestionsGroupId(groupId);
    };

    const onChangeSearchBarInput = (value) => {
        setSearchBarInput(value);
    };

    const handleFilterClick = (key, data) => {
        if (key === 'SORT') {
            setSortKey(data === sortKey ? '' : data);
        } else {
            const hasItem = statusKey.includes(data);
            let newData = statusKey;
            if (hasItem) {
                newData = newData.filter((item) => item !== data);
            } else {
                newData = [...statusKey, data];
            }
            setStatusKey(newData);
        }
    };

    const handleResetFilterSort = () => {
        setSortKey('none');
        setStatusKey([]);
    };

    const getCourseProgress = () => {
        if (isLoading) return Number(0).toFixed(1);
        return Number(getAverage(distribution, groups)).toFixed(1);
    };

    const getGroupList = () => {
        return (groups || [])
            .map((group) => {
                const members = studentAccountUsers.filter((student) => student.group_id === group.id);
                return {
                    ...group,
                    members: members,
                    progress: getProgress(distribution, group.id),
                    status: getStatus(distribution, groups, group.id),
                };
            })
            .filter(({ status }) => {
                return statusKey?.length ? statusKey.includes(status) : true;
            })
            .filter(({ name, members }) => {
                if (searchBarInput) {
                    const searchValue = searchBarInput.toLowerCase();
                    const hasMembers = members.filter((member) =>
                        getUserPreferredFullName(member).toLowerCase().includes(searchValue)
                    );
                    if (hasMembers.length || name.toLowerCase().includes(searchValue)) return true;
                } else {
                    return true;
                }
            })
            .sort((a, b) => {
                if (sortKey === 'highestPercentage') {
                    return b.progress - a.progress;
                } else if (sortKey === 'lowestPercentage') {
                    return a.progress - b.progress;
                } else if (sortKey === 'titlea-z') {
                    const titleTeamA = a.members.length === 1 ? getUserPreferredFullName(a.members[0]) : a.name;
                    const titleTeamB = b.members.length === 1 ? getUserPreferredFullName(b.members[0]) : b.name;
                    return titleTeamA.toLowerCase() < titleTeamB.toLowerCase() ? -1 : 1;
                } else if (sortKey === 'titlez-a') {
                    const titleTeamA = a.members.length === 1 ? a.members[0].first_name : a.name;
                    const titleTeamB = b.members.length === 1 ? b.members[0].first_name : b.name;
                    return titleTeamA.toLowerCase() < titleTeamB.toLowerCase() ? 1 : -1;
                } else {
                    return Number(a.number) - Number(b.number);
                }
            });
    };

    useEffect(() => {
        (async () => {
            if (course?.id) {
                const distribution = await courseService.findCourseDistribution(course.id);
                setDistribution(distribution);
            }
        })();
    }, [course?.id]);

    useEffect(() => {
        if ((isFilterDrawerOpen && sortKey) || statusKey?.length > 0) {
            (async () => {
                if (
                    auth.filter_keys?.group_overview?.sort_key === sortKey &&
                    auth.filter_keys?.group_overview?.status_key === statusKey
                )
                    return;
                await accountService.updateAccount(auth.owner_account_id, {
                    filter_keys: { group_overview: { sort_key: sortKey, status_key: statusKey } },
                });
                setAuth((prev) => ({
                    ...prev,
                    filter_keys: { group_overview: { sort_key: sortKey, status_key: statusKey } },
                }));
            })();
        }
    }, [auth, isFilterDrawerOpen, sortKey, statusKey]);

    useEffect(() => {
        if (auth?.filter_keys?.group_overview) {
            setSortKey(auth.filter_keys.group_overview.sort_key || 'none');
            setStatusKey(auth.filter_keys.group_overview.status_key || []);
        }
    }, [auth.filter_keys]);

    const tabList = [
        {
            key: 'all-teams',
            value: 'All Teams',
            content: (
                <AllTeams
                    groupList={getGroupList()}
                    suggestionsList={suggestions}
                    handleSetGroupId={setCurrentGroupId}
                    viewSuggestions={viewSuggestions}
                />
            ),
        },
        {
            key: 'suggested-teams',
            value: 'Review Suggested',
            content: (
                <SuggestedTeams
                    initialSuggestionsGroupId={initialSuggestionsGroupId}
                    groupList={getGroupList()}
                    suggestionsList={suggestions}
                    searchKey={searchBarInput}
                    statusKey={statusKey}
                    sortKey={sortKey}
                    cleanUp={() => setInitialSuggestionsGroupId('')}
                />
            ),
        },
    ];

    const props = {
        course,
        coursePercentage: getCourseProgress(),
        setIsFilterDrawerOpen,
        tabList,
        defaultRoute,
        setDefaultRoute,
        searchBarInput,
        onChangeSearchBarInput,
    };

    const filterDrawerProps = {
        open: isFilterDrawerOpen,
        onClose: () => setIsFilterDrawerOpen(false),
    };

    const FilterDrawerInner = (
        <FilterSortGroupOverview
            sortKey={sortKey}
            statusKey={statusKey}
            onClick={handleFilterClick}
            onClose={() => setIsFilterDrawerOpen(false)}
            resetFilterSort={handleResetFilterSort}
        />
    );

    const responsiveFilterDrawers = {
        mobile: (
            <Drawer anchor={'left'} device={'mobile'} {...filterDrawerProps}>
                {FilterDrawerInner}
            </Drawer>
        ),
        tablet: (
            <Drawer anchor={'left'} device={'tablet'} {...filterDrawerProps}>
                {FilterDrawerInner}
            </Drawer>
        ),
        desktop: (
            <Drawer anchor={'left'} device={'tablet'} {...filterDrawerProps}>
                {FilterDrawerInner}
            </Drawer>
        ),
    };

    const responsiveLayouts = {
        mobile: <Mobile {...props} />,
        tablet: <Tablet {...props} />,
        desktop: <Desktop {...props} />,
    };

    return isLoading ? (
        <LogoLoading />
    ) : (
        <>
            {!groups.length ? (
                <CustomStack alignItems={'center'} spacing={3}>
                    <CustomBox>{NoGroups}</CustomBox>
                    <Text
                        align={'center'}
                        color={(theme) => theme.palette.ensightful.neutral[700]}
                        variant={'h6'}
                        weight={'semibold'}
                    >
                        Get Started by Setting up Teams!
                    </Text>
                    <Text align={'center'} color={(theme) => theme.palette.ensightful.neutral[700]} variant={'p2'}>
                        Looks like you don’t have any teams set up yet. Navigate to the “People” tab to split up your
                        class into teams for the project.
                    </Text>
                </CustomStack>
            ) : (
                <>
                    {withResponsiveLayout(width, responsiveLayouts)}
                    {withResponsiveLayout(width, responsiveFilterDrawers)}
                </>
            )}
        </>
    );
};

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        maxWidth: 'min(31.25rem, 100%)',
        padding: theme.spacing('16vh', 4, 0),
    };
});

const CustomBox = styled(Box)(() => {
    return {
        width: '100%',
        '& > svg': {
            width: '100%',
        },
    };
});

export default GroupOverview;
